'use client';

import { Check } from 'lucide-react';
import { useEffect, useRef } from 'react';

type CheckboxVariant = 'default' | 'transparent';

type CheckboxProps = {
  label?: string;
  children?: React.ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  className?: string;
  variant?: CheckboxVariant;
  indeterminate?: boolean;
  id?: string;
  disabled?: boolean;
};

export const Checkbox = ({
  label,
  children,
  checked,
  onChange,
  className,
  variant = 'default',
  indeterminate,
  disabled = false,
  id,
}: CheckboxProps) => {
  const checkboxRef = useRef<HTMLInputElement>(null);
  const checkboxId = id || `${label}-checkbox`;

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate || false;
    }
  }, [indeterminate]);

  const renderContent = () => {
    if (children) {
      return children;
    }
    if (label) {
      return (
        <label
          className={`text-[14px] text-body-md ${variant === 'default' ? 'text-[#1F202C]' : 'text-white'} hover:cursor-pointer`}
          htmlFor={checkboxId}
        >
          {label}
        </label>
      );
    }
    return null;
  };

  return (
    <div className="flex items-center py-2 px-2 gap-2 hover:cursor-pointer">
      <button
        type="button"
        id={checkboxId}
        role="checkbox"
        aria-checked={indeterminate ? 'mixed' : checked}
        tabIndex={0}
        onClick={() => onChange(!checked)}
        disabled={disabled}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onChange(!checked);
          }
        }}
        className={`h-5 w-5 flex items-center justify-center rounded-[4px] border hover:cursor-pointer ${
          variant === 'default'
            ? `border-[#D4D6EC] ${checked ? 'bg-black border-black' : 'bg-white'}`
            : `${checked ? 'bg-white' : 'bg-transparent border-[#828282]'}`
        } ${className}`}
      >
        {checked && <Check size={16} color={variant === 'default' ? 'white' : 'black'} strokeWidth={2} className="w-full h-full" />}
      </button>
      {renderContent()}
    </div>
  );
};
